import { findEarliestSignedUrlExpiry } from '#pplx/frontend/common/files/urls.js';
// The backend keeps an existing asset signature until it is within 10 minutes of expiry
// (`_RESIGN_BUFFER_SECONDS` in common/core/url_signing/sign_asset.py), so a fresh response can
// carry URLs with little more than 10 minutes left. Go stale ahead of the earliest one instead of
// assuming every URL lives for the full presign lifetime.
const REFRESH_MARGIN_MS = 1000 * 60 * 5;
// Bounds refetches when a response carries a short-lived URL the backend does not re-sign.
const MIN_REFRESH_MS = 1000 * 60 * 5;
const MAX_REFRESH_MS = 1000 * 60 * 50;
// Keyed by the cache entry so the deadline lives and is collected with the query. Streaming
// `setQueryData` updates reset `dataUpdatedAt`, so the deadline is stored as an absolute time
// that only moves when a fetch returns.
const refreshAtByQuery = new WeakMap();
/**
 * `staleTime` for a query whose data carries presigned URLs: the query goes stale ahead of the
 * earliest expiry recorded by `recordSignedUrlFetch`, so mount and focus refetches re-sign it.
 */
export function getSignedUrlStaleTime(query) {
    const refreshAt = refreshAtByQuery.get(query) ?? query.state.dataUpdatedAt + MAX_REFRESH_MS;
    return Math.max(0, refreshAt - query.state.dataUpdatedAt);
}
/**
 * Records the deadline `REFRESH_MARGIN_MS` before the earliest URL in `fetched` expires, clamped
 * to `MIN_REFRESH_MS`–`MAX_REFRESH_MS`. Call at the end of the query function. Pass only data this
 * fetch signed: cached data a refetch does not re-sign would make the query stale for nothing.
 * URLs already expired at `fetchedAt` are ignored for the same reason.
 */
export function recordSignedUrlFetch(queryClient, queryKey, fetched, fetchedAt) {
    const query = queryClient.getQueryCache().find({ queryKey, exact: true });
    if (!query)
        return;
    const earliestExpiry = findEarliestSignedUrlExpiry(fetched, fetchedAt);
    const refreshInMs = earliestExpiry === undefined ? MAX_REFRESH_MS : earliestExpiry - REFRESH_MARGIN_MS - fetchedAt;
    refreshAtByQuery.set(query, fetchedAt + Math.min(MAX_REFRESH_MS, Math.max(MIN_REFRESH_MS, refreshInMs)));
}
//# sourceMappingURL=signedUrlRefresh.js.map